import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import { Activity, ArrowLeft, Blocks, Braces, CheckCircle2, FilePlus2, FlaskConical, History, Play, Rocket, Save, Trash2, UserRound, Workflow, XCircle, Zap } from "lucide-react";
import { usePermission } from "@ncp/api-client";
import { LogicBuilder, describeLogic, type Condition, type LogicField } from "@ncp/forms";
import { formatRelative, useLocale, useTranslation } from "@ncp/i18n";
import {
  Badge, Button, Card, CardContent, CardDescription, CardHeader, CardTitle, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, EmptyState, Field,
  InlineAlert, Input, NativeSelect, Skeleton, Switch, Tabs, TabsList, TabsTrigger, Textarea, Tooltip, cn, notifyDone, notifySaved, toast,
} from "@ncp/ui";
import { useBreadcrumbs, useHelpTopic, usePaletteActions } from "../app/console-context";
import { CodeEditor } from "../components/CodeEditor";
import { UnsavedChangesDialog, useUnsavedChanges } from "../lib/useUnsavedChanges";
import { stableJson, useHistory, useUndoShortcuts } from "../features/forms/model";
import { VersionsPanel } from "../features/forms/VersionsPanel";
import {
  automationBodyOf, useAutomation, useAutomationActions, useAutomationCheck, useAutomationOptions,
  type AutomationBody, type AutomationDetail, type AutomationEditorOptions, type AutomationRun,
} from "../features/automations/api";
import { ActionsEditor } from "../features/automations/ActionsEditor";
import { TriggerEditor } from "../features/automations/TriggerEditor";
import { RUN_VARIANT, RunsTable } from "../features/automations/RunsTable";

type Tab = "design" | "runs" | "code" | "versions";
const TABS: Array<{ key: Tab; icon: typeof Workflow }> = [{ key: "design", icon: Workflow }, { key: "runs", icon: Activity }, { key: "code", icon: Braces }, { key: "versions", icon: History }];
const STATUS_VARIANT: Record<string, "success" | "warning" | "info" | "default"> = { Published: "success", InReview: "warning", Draft: "info", Retired: "default" };
const SCOPE_ICON = { Platform: Zap, Workspace: Blocks, Personal: UserRound } as const;
const GUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const CASE_NUMBER = /^[a-z]{2,6}-\d{4}-\d{3,8}$/i;

function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value);
  useEffect(() => { const id = setTimeout(() => setV(value), ms); return () => clearTimeout(id); }, [value, ms]);
  return v;
}

/** Automation editor: when it runs, the condition, what it does, scope and throttling — checked live by the server — plus runs, code and versions. */
export function AutomationEditorPage() {
  const { key = "" } = useParams();
  const [params, setParams] = useSearchParams();
  const version = params.get("version") ? Number(params.get("version")) : undefined;
  const tab = (params.get("tab") as Tab | null) ?? "design";
  const setTab = (next: Tab) => setParams((p) => { p.set("tab", next); return p; }, { replace: true });
  const detail = useAutomation(key, version);

  if (detail.isLoading) return <div className="space-y-3"><Skeleton className="h-10 w-1/2" /><Skeleton className="h-[60vh]" /></div>;
  if (detail.isError || !detail.data) return <Missing automationKey={key} />;
  return <Editor key={detail.data.automation.id} detail={detail.data} tab={tab} onTab={setTab} onOpenVersion={(v) => setParams((p) => { p.set("version", String(v)); return p; })} />;
}

function Missing({ automationKey }: { automationKey: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  useBreadcrumbs([{ label: t("admin:nav.automations"), href: "/automations" }, { label: automationKey }]);
  return <EmptyState icon={XCircle} title={t("admin:automations.editor.notFound")} description={t("admin:automations.editor.notFoundBody", { key: automationKey })} action={<Button variant="outline" onClick={() => navigate("/automations")}><ArrowLeft className="rtl:rotate-180" /> {t("admin:common.back")}</Button>} />;
}

function Editor({ detail, tab, onTab, onOpenVersion }: { detail: AutomationDetail; tab: Tab; onTab: (t: Tab) => void; onOpenVersion: (v: number) => void }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const navigate = useNavigate();
  const { can } = usePermission();
  const a = detail.automation;
  const canWrite = can("platform.automations.write");
  const canRun = can("platform.automations.run");
  const personal = a.scope === "Personal";
  const editable = canWrite && a.status === "Draft" && !personal;
  const actions = useAutomationActions();
  const options = useAutomationOptions();

  const history = useHistory<AutomationBody>(automationBodyOf(a));
  useUndoShortcuts(history);
  const body = history.present;
  const savedJson = useRef(stableJson(automationBodyOf(a)));
  const dirty = stableJson(body) !== savedJson.current;
  const blocker = useUnsavedChanges(dirty);
  const checked = useDebounced(body, 400);
  const check = useAutomationCheck(personal ? null : checked);
  const issues = check.data?.issues ?? [];
  const [confirm, setConfirm] = useState<"discard" | "stage" | "newVersion" | null>(null);
  const [running, setRunning] = useState(false);
  const [note, setNote] = useState("");

  const live = detail.versions.find((v) => v.status === "Published");
  const draft = detail.versions.find((v) => v.status === "Draft" || v.status === "InReview");
  const title = (locale === "ar" && body.nameAr ? body.nameAr : body.name) || a.key;
  const Icon = SCOPE_ICON[a.scope];
  useBreadcrumbs(useMemo(() => [{ label: t("admin:nav.automations"), href: "/automations" }, { label: title }], [t, title]));
  useHelpTopic({ title: t("admin:automations.editor.helpTitle"), docsAnchor: "automations", body: (<><p>{t("admin:automations.help.what")}</p><p>{t("admin:automations.help.condition")}</p><p>{t("admin:automations.help.safety")}</p><p>{t("admin:assistants.help.versions")}</p></>) });

  const patch = useCallback((p: Partial<AutomationBody>) => { if (editable) history.set((b) => ({ ...b, ...p })); }, [editable, history]);

  /** Saves the draft; false when it could not be saved (the save reported why), so staging does not go on with the old version. */
  const save = useCallback(async (): Promise<boolean> => {
    if (!editable || !dirty) return true;
    if (!body.name.trim()) { toast.error(t("admin:automations.editor.nameRequired")); return false; }
    try {
      const saved = await actions.update.mutateAsync({ id: a.id, body, changeNote: note || undefined });
      savedJson.current = stableJson(automationBodyOf(saved));
      history.reset(automationBodyOf(saved));
      setNote("");
      notifySaved(saved, t("admin:common.saved"));
      return true;
    } catch { return false; /* useSave reported the failure with its cause */ }
  }, [a.id, actions.update, body, dirty, editable, history, note, t]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "s") { e.preventDefault(); void save(); } };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [save]);

  usePaletteActions(useMemo(() => [
    ...(editable ? [{ id: "automations.save", label: t("admin:common.save"), icon: Save, permission: "platform.automations.write", run: () => void save() }] : []),
    ...(canRun && a.status === "Published" ? [{ id: "automations.run", label: t("admin:automations.run.title"), icon: Play, permission: "platform.automations.run", run: () => setRunning(true) }] : []),
  ], [a.status, canRun, editable, save, t]));

  // The actions' failures are reported by useSave with their cause; the catches only stop what follows.
  const stage = async () => {
    if (dirty && !(await save())) return;
    try {
      const cs = await actions.stage.mutateAsync({ id: a.id, titleHint: t("admin:automations.editor.stageTitle", { name: title }) });
      notifySaved(cs, t("admin:forms.builder.staged"));
      setConfirm(null);
      navigate(`/releases/${cs.id}`);
    } catch { /* reported */ }
  };
  const newVersion = async () => {
    try {
      const d = await actions.newVersion.mutateAsync({ key: a.key, changeNote: note || undefined });
      setConfirm(null); setNote("");
      notifySaved(d, t("admin:forms.builder.versionCreated", { version: d.version }));
      onOpenVersion(d.version);
    } catch { /* reported */ }
  };
  const discard = async () => {
    try { const r = await actions.discard.mutateAsync(a.id); notifySaved(r, t("admin:forms.builder.discarded")); navigate(live ? `/automations/${encodeURIComponent(a.key)}` : "/automations"); setConfirm(null); } catch { /* reported */ }
  };
  const withdraw = async () => {
    try { const r = await actions.withdraw.mutateAsync(a.id); notifySaved(r, t("admin:forms.builder.withdrawn")); } catch { /* reported */ }
  };
  const toggle = (enabled: boolean) => actions.setEnabled.mutate({ id: a.id, enabled }, {
    onSuccess: (answer) => notifySaved(answer, enabled ? t("admin:automations.editor.resumed") : t("admin:automations.editor.paused")),
  });

  return (
    <div className="-mt-2">
      <UnsavedChangesDialog blocker={blocker} />
      <div className="sticky top-14 z-20 -mx-4 mb-3 border-b border-sand-200 bg-background/90 px-4 py-2 backdrop-blur sm:-mx-6 sm:px-6">
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => navigate("/automations")} aria-label={t("admin:common.back")}><ArrowLeft className="rtl:rotate-180" /></Button>
          <span className="grid size-9 shrink-0 place-items-center rounded-xl bg-aegold-50 text-aegold-700"><Icon className="size-4" /></span>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-1.5">
              <h1 className="truncate text-base font-semibold">{title}</h1>
              <Badge variant="outline" size="sm" className="font-mono">{a.key}</Badge>
              <Badge variant="default" size="sm">v{a.version}</Badge>
              <Badge variant={STATUS_VARIANT[a.status] ?? "default"} size="sm" dot>{t(`admin:forms.status.${a.status}`)}</Badge>
              <Badge variant="outline" size="sm">{t(`admin:automations.scope.${a.scope}`)}</Badge>
              {a.workspaceKey ? <Badge variant="outline" size="sm" className="font-mono">{a.workspaceKey}</Badge> : null}
              {a.status === "Published" ? <Badge variant={a.enabled ? "success" : "default"} size="sm">{a.enabled ? t("admin:automations.state.on") : t("admin:automations.state.paused")}</Badge> : null}
              {dirty ? <Badge variant="warning" size="sm">{t("admin:common.unsaved")}</Badge> : null}
              {!personal && issues.length ? <Tooltip content={issues[0]}><Badge variant="danger" size="sm">{t("admin:common.issues", { count: issues.length })}</Badge></Tooltip> : null}
            </div>
            <p className="truncate text-xs text-muted-foreground">{(locale === "ar" ? check.data?.whenAr ?? a.whenAr : check.data?.when ?? a.when)}</p>
          </div>
          <div className="ms-auto flex flex-wrap items-center gap-1">
            {a.status === "Published" && canWrite ? (
              <Tooltip content={a.enabled ? t("admin:automations.editor.pause") : t("admin:automations.editor.resume")}>
                <span className="inline-flex items-center px-1"><Switch checked={a.enabled} disabled={actions.setEnabled.isPending} aria-label={t("admin:automations.editor.pause")} onCheckedChange={toggle} /></span>
              </Tooltip>
            ) : null}
            {a.status === "Published" && canRun ? <Button size="sm" variant="outline" onClick={() => setRunning(true)}><Play /> {t("admin:automations.run.title")}</Button> : null}
            {editable ? (<>
              <Button size="sm" variant="ghost" className="text-danger-600" onClick={() => setConfirm("discard")}><Trash2 /> {t("admin:forms.builder.discard")}</Button>
              <Button size="sm" variant={dirty ? "gold" : "outline"} loading={actions.update.isPending} disabled={!dirty} onClick={() => void save()}><Save /> {t("admin:common.save")}</Button>
              <Button size="sm" variant="default" disabled={issues.length > 0 || check.isFetching} onClick={() => setConfirm("stage")}><Rocket /> {t("admin:forms.builder.stage")}</Button>
            </>) : null}
            {canWrite && !personal && a.status === "InReview" ? <Button size="sm" variant="outline" onClick={withdraw} loading={actions.withdraw.isPending}>{t("admin:forms.builder.withdraw")}</Button> : null}
            {canWrite && !personal && (a.status === "Published" || a.status === "Retired") && !draft ? <Button size="sm" variant="gold" onClick={() => setConfirm("newVersion")}><FilePlus2 /> {t("admin:forms.builder.newVersion")}</Button> : null}
            {(a.status === "Published" || a.status === "Retired") && draft ? <Button size="sm" variant="outline" onClick={() => onOpenVersion(draft.version)}>{t("admin:automations.editor.openDraft", { version: draft.version })}</Button> : null}
          </div>
        </div>
        <Tabs value={tab} onValueChange={(v) => onTab(v as Tab)} className="mt-2">
          <TabsList>{TABS.filter((x) => !(personal && x.key === "versions")).map(({ key, icon: TabIcon }) => (
            <TabsTrigger key={key} value={key}><TabIcon className="size-3.5" /> {t(`admin:automations.tabs.${key}`)}{key === "runs" ? <Badge variant="outline" size="sm" className="ms-1">{detail.runs.length}</Badge> : null}</TabsTrigger>
          ))}</TabsList>
        </Tabs>
      </div>

      {personal ? <InlineAlert tone="info" className="mb-3">{t("admin:automations.editor.personalNotice", { owner: a.ownerName ?? "—" })}</InlineAlert> : null}
      {!personal && a.status === "Draft" && !canWrite ? <InlineAlert tone="info" className="mb-3">{t("admin:common.permissionDenied")}</InlineAlert> : null}
      {a.status === "InReview" ? <InlineAlert tone="warning" className="mb-3">{t("admin:forms.builder.inReviewNotice")}</InlineAlert> : null}
      {a.status === "Draft" && live && !personal ? (
        <InlineAlert tone="info" className="mb-3">{t("admin:automations.editor.liveNotice", { version: live.version })} <button type="button" className="font-medium underline" onClick={() => onOpenVersion(live.version)}>{t("admin:automations.editor.openLive")}</button></InlineAlert>
      ) : null}
      {a.pausedReason ? <InlineAlert tone="warning" className="mb-3">{a.pausedReason}</InlineAlert> : null}

      <div className="mx-auto max-w-[1400px]">
        {tab === "design" ? <DesignTab body={body} patch={patch} readOnly={!editable} options={options.data} detail={detail} check={check.data} checking={check.isFetching} personal={personal} /> : null}
        {tab === "runs" ? <RunsTable runs={detail.runs} showAutomation={false} /> : null}
        {tab === "code" ? <CodeTab body={body} onChange={(b) => patch(b)} readOnly={!editable} /> : null}
        {tab === "versions" && !personal ? <VersionsPanel formKey={a.key} resource="automations" currentVersion={a.version} onOpenVersion={onOpenVersion} /> : null}
      </div>

      <Dialog open={confirm !== null} onOpenChange={(o) => { if (!o) setConfirm(null); }}>
        <DialogContent size="sm">
          <DialogHeader>
            <DialogTitle>{confirm === "discard" ? t("admin:forms.builder.discardTitle") : confirm === "stage" ? t("admin:forms.builder.stageDialogTitle") : t("admin:forms.builder.newVersionTitle")}</DialogTitle>
            <DialogDescription>{confirm === "discard" ? t("admin:forms.builder.discardBody") : confirm === "stage" ? t("admin:automations.editor.stageBody") : t("admin:automations.editor.newVersionBody", { version: a.version + 1 })}</DialogDescription>
          </DialogHeader>
          {confirm !== "discard" ? <Field label={t("admin:common.changeNote")} hint={t("admin:common.changeNoteHint")}><Textarea value={note} onChange={(e) => setNote(e.target.value)} className="min-h-[72px]" /></Field> : null}
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirm(null)}>{t("admin:common.cancel")}</Button>
            {confirm === "discard" ? <Button variant="destructive" loading={actions.discard.isPending} onClick={discard}>{t("admin:forms.builder.discard")}</Button> : null}
            {confirm === "stage" ? <Button variant="gold" loading={actions.stage.isPending} onClick={stage}><Rocket /> {t("admin:forms.builder.stage")}</Button> : null}
            {confirm === "newVersion" ? <Button variant="gold" loading={actions.newVersion.isPending} onClick={newVersion}><FilePlus2 /> {t("admin:forms.builder.newVersion")}</Button> : null}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <RunDialog open={running} onOpenChange={setRunning} automationId={a.id} runs={detail.runs} />
    </div>
  );
}

function DesignTab({ body, patch, readOnly, options, detail, check, checking, personal }: {
  body: AutomationBody; patch: (p: Partial<AutomationBody>) => void; readOnly: boolean; options: AutomationEditorOptions | undefined; detail: AutomationDetail;
  check: ReturnType<typeof useAutomationCheck>["data"]; checking: boolean; personal: boolean;
}) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const a = detail.automation;
  const fields = useConditionFields(options);
  const fmt = useMemo(() => new Intl.DateTimeFormat(locale === "ar" ? "ar-AE" : "en-GB", { dateStyle: "medium", timeStyle: "short", timeZone: "Asia/Dubai" }), [locale]);
  const then = check ? (locale === "ar" ? check.thenAr : check.then) : (locale === "ar" ? a.thenAr : a.then);
  return (
    <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_340px]">
      <div className="space-y-4">
        <Card>
          <CardHeader><CardTitle>{t("admin:automations.editor.when")}</CardTitle><CardDescription>{t("admin:automations.editor.whenHint")}</CardDescription></CardHeader>
          <CardContent><TriggerEditor body={body} patch={patch} options={options} readOnly={readOnly} /></CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle>{t("admin:automations.editor.onlyIf")}</CardTitle><CardDescription>{t("admin:automations.editor.onlyIfHint")}</CardDescription></CardHeader>
          <CardContent>
            {readOnly ? (
              body.condition == null
                ? <p className="text-sm text-muted-foreground">{t("admin:automations.editor.noCondition")}</p>
                : <p className="rounded-md border bg-sand-50 px-3 py-2 text-sm">{describeLogic(body.condition as Condition, fields, locale)}</p>
            ) : (
              <LogicBuilder value={(body.condition ?? undefined) as Condition | undefined} onChange={(c) => patch({ condition: c ?? null })} fields={fields} locale={locale} emptyLabel={t("admin:automations.editor.noCondition")} />
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle>{t("admin:automations.editor.then")}</CardTitle><CardDescription>{t("admin:automations.editor.thenHint")}</CardDescription></CardHeader>
          <CardContent><ActionsEditor actions={body.actions} onChange={(next) => patch({ actions: next })} scope={body.scope} workspaceKey={body.workspaceKey} options={options} readOnly={readOnly} locale={locale} /></CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle>{t("admin:automations.editor.settings")}</CardTitle><CardDescription>{t("admin:automations.editor.settingsHint")}</CardDescription></CardHeader>
          <CardContent className="grid gap-3 sm:grid-cols-2">
            <Field label={t("admin:common.name")} required><Input value={body.name} readOnly={readOnly} onChange={(e) => patch({ name: e.target.value })} /></Field>
            <Field label={t("admin:common.nameAr")}><Input value={body.nameAr ?? ""} dir="rtl" readOnly={readOnly} onChange={(e) => patch({ nameAr: e.target.value || null })} /></Field>
            <Field label={t("admin:common.description")} className="sm:col-span-2"><Textarea value={body.description ?? ""} readOnly={readOnly} className="min-h-[64px]" onChange={(e) => patch({ description: e.target.value || null })} /></Field>
            {!personal ? (
              <Field label={t("admin:automations.columns.scope")} hint={t(`admin:automations.create.scopeHint.${body.scope}`)}>
                <NativeSelect value={body.scope} disabled={readOnly} onChange={(e) => { const scope = e.target.value as AutomationBody["scope"]; patch({ scope, workspaceKey: scope === "Workspace" ? body.workspaceKey : null }); }}>
                  <option value="Platform">{t("admin:automations.scope.Platform")}</option>
                  <option value="Workspace">{t("admin:automations.scope.Workspace")}</option>
                </NativeSelect>
              </Field>
            ) : null}
            {body.scope === "Workspace" ? (
              <Field label={t("admin:automations.fields.workspace")} required>
                <NativeSelect value={body.workspaceKey ?? ""} disabled={readOnly} onChange={(e) => patch({ workspaceKey: e.target.value || null })}>
                  <option value="">—</option>
                  {(options?.workspaces ?? []).map((w) => <option key={w.key} value={w.key}>{locale === "ar" && w.nameAr ? w.nameAr : w.name}</option>)}
                </NativeSelect>
              </Field>
            ) : null}
            <Field label={t("admin:automations.fields.throttle")} hint={t("admin:automations.fields.throttleHint")}>
              <Input type="number" min={0} value={body.throttleMinutes} readOnly={readOnly} onChange={(e) => patch({ throttleMinutes: Math.max(0, Number(e.target.value) || 0) })} />
            </Field>
            <Field label={t("admin:common.enabled")} hint={t("admin:automations.fields.enabledHint")}><div className="flex h-10 items-center"><Switch checked={body.enabled} disabled={readOnly} onCheckedChange={(v) => patch({ enabled: v })} /></div></Field>
          </CardContent>
        </Card>
      </div>

      <div className="space-y-4 lg:sticky lg:top-40 lg:self-start">
        <Card>
          <CardHeader><CardTitle>{t("admin:automations.editor.summary")}</CardTitle><CardDescription>{checking ? t("admin:automations.editor.checking") : t("admin:automations.editor.summaryHint")}</CardDescription></CardHeader>
          <CardContent className="space-y-3 text-sm">
            <div><p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{t("admin:automations.editor.when")}</p><p>{check ? (locale === "ar" ? check.whenAr : check.when) : (locale === "ar" ? a.whenAr : a.when)}</p></div>
            {body.condition != null ? <div><p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{t("admin:automations.editor.onlyIf")}</p><p className="text-sm">{describeLogic(body.condition as Condition, fields, locale)}</p></div> : null}
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{t("admin:automations.editor.then")}</p>
              {then.length ? <ol className="list-decimal space-y-0.5 ps-4">{then.map((x, i) => <li key={i}>{x}</li>)}</ol> : <p className="text-muted-foreground">—</p>}
            </div>
            {check?.nextRuns.length ? <div><p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{t("admin:automations.schedule.next")}</p><ul className="text-xs">{check.nextRuns.map((n) => <li key={n}>{fmt.format(new Date(n))}</li>)}</ul></div> : null}
            {personal ? null : check && check.issues.length === 0 ? (readOnly ? null :
              <InlineAlert tone="success"><span className="inline-flex items-center gap-1.5"><CheckCircle2 className="size-4" /> {t("admin:automations.editor.ready")}</span></InlineAlert>
            ) : check ? (
              <InlineAlert tone="danger"><ul className="list-disc space-y-0.5 ps-4 text-xs">{check.issues.map((x) => <li key={x}>{x}</li>)}</ul></InlineAlert>
            ) : null}
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle>{t("admin:automations.editor.activity")}</CardTitle></CardHeader>
          <CardContent className="space-y-1.5 text-xs">
            <Line label={t("admin:automations.editor.runs")} value={String(a.runCount)} />
            <Line label={t("admin:automations.editor.failures")} value={String(a.failureCount)} />
            <Line label={t("admin:automations.columns.lastRun")} value={a.lastRunAt ? formatRelative(a.lastRunAt, locale) : t("admin:automations.neverRan")} badge={a.lastStatus ? <Badge variant={RUN_VARIANT[a.lastStatus] ?? "default"} size="sm">{t(`admin:automations.runStatus.${a.lastStatus}`, { defaultValue: a.lastStatus })}</Badge> : null} />
            {a.nextRunAt && a.enabled ? <Line label={t("admin:automations.editor.nextRun")} value={fmt.format(new Date(a.nextRunAt))} /> : null}
            {a.ownerName ? <Line label={t("admin:automations.editor.owner")} value={a.ownerName} /> : null}
            <Line label={t("admin:common.createdBy")} value={`${a.createdByName ?? "—"} · ${formatRelative(a.createdAt, locale)}`} />
            {a.threadId ? <p className="pt-1 text-muted-foreground">{t("admin:automations.editor.fromChat")}</p> : null}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function Line({ label, value, badge }: { label: string; value: string; badge?: React.ReactNode }) {
  return <div className="flex items-center justify-between gap-2"><span className="text-muted-foreground">{label}</span><span className="flex items-center gap-1 text-end font-medium">{badge}{value}</span></div>;
}

/** What a condition may test: the case or matter the event concerns, and the event's own fields (clock, document, draft). */
function useConditionFields(options: AutomationEditorOptions | undefined): LogicField[] {
  const { t } = useTranslation();
  const { locale } = useLocale();
  return useMemo(() => {
    const name = (r: { name: string; nameAr?: string | null }) => (locale === "ar" && r.nameAr ? r.nameAr : r.name);
    const v = (k: string) => t(`admin:automations.vars.${k}`);
    return [
      { path: "priority", label: v("priority"), type: "string", options: ["Low", "Medium", "High", "Critical"].map((p) => ({ value: p, label: t(`admin:automations.priority.${p}`) })) },
      { path: "serviceKey", label: v("serviceKey"), type: "string", options: (options?.playbooks ?? []).map((p) => ({ value: p.key, label: name(p) })) },
      { path: "state", label: v("state"), type: "string" },
      { path: "status", label: v("status"), type: "string" },
      { path: "workspaceKey", label: v("workspaceKey"), type: "string", options: (options?.workspaces ?? []).map((w) => ({ value: w.key, label: name(w) })) },
      { path: "assigneeUserId", label: v("assigneeUserId"), type: "string" },
      { path: "isMatter", label: v("isMatter"), type: "boolean" },
      { path: "title", label: v("title"), type: "string" },
      { path: "daysRemaining", label: v("daysRemaining"), type: "number" },
      { path: "clock", label: v("clock"), type: "string" },
      { path: "category", label: v("category"), type: "string" },
      { path: "fromApplicant", label: v("fromApplicant"), type: "boolean" },
      { path: "agentKey", label: v("agentKey"), type: "string", options: (options?.agents ?? []).map((ag) => ({ value: ag.key, label: name(ag) })) },
      { path: "confidence", label: v("confidence"), type: "number" },
      { path: "urgency", label: v("urgency"), type: "string" },
      { path: "triggerSource", label: v("triggerSource"), type: "string", options: ["Chat", "Workflow", "Automation", "Manual"].map((s) => ({ value: s, label: s })) },
    ];
  }, [locale, options, t]);
}

function CodeTab({ body, onChange, readOnly }: { body: AutomationBody; onChange: (b: AutomationBody) => void; readOnly: boolean }) {
  const { t } = useTranslation();
  const [text, setText] = useState(() => JSON.stringify(body, null, 2));
  const [error, setError] = useState<string | null>(null);
  useEffect(() => { setText(JSON.stringify(body, null, 2)); setError(null); }, [body]);
  const apply = () => {
    try { const parsed = JSON.parse(text) as AutomationBody; onChange({ ...body, ...parsed }); setError(null); notifyDone(t("admin:assistants.code.applied")); } catch (e) { setError((e as Error).message); }
  };
  return (
    <Card>
      <CardHeader className="flex-row items-start justify-between gap-3">
        <div><CardTitle>{t("admin:automations.tabs.code")}</CardTitle><CardDescription>{t("admin:automations.editor.codeHint")}</CardDescription></div>
        {!readOnly ? <Button size="sm" variant="gold" onClick={apply}>{t("admin:assistants.code.apply")}</Button> : null}
      </CardHeader>
      <CardContent>
        <CodeEditor value={text} onChange={readOnly ? undefined : setText} readOnly={readOnly} height="60vh" />
        {error ? <p className="mt-2 text-xs text-danger-600">{error}</p> : null}
      </CardContent>
    </Card>
  );
}

/** Runs the published version now — for real, or as a dry run that records what it would do and changes nothing. */
function RunDialog({ open, onOpenChange, automationId, runs }: { open: boolean; onOpenChange: (o: boolean) => void; automationId: string; runs: AutomationRun[] }) {
  const { t } = useTranslation();
  const actions = useAutomationActions();
  const recent = useMemo(() => [...new Map(runs.filter((r) => r.caseId && r.caseNumber).map((r) => [r.caseId!, r.caseNumber!])).entries()].slice(0, 5), [runs]);
  const [caseRef, setCaseRef] = useState("");
  const [dryRun, setDryRun] = useState(true);
  const [result, setResult] = useState<AutomationRun | null>(null);
  useEffect(() => { if (open) setCaseRef(recent[0]?.[1] ?? ""); }, [open, recent]);
  const ref = caseRef.trim();
  const caseError = ref && !GUID.test(ref) && !CASE_NUMBER.test(ref) ? t("admin:automations.run.caseInvalid") : undefined;
  const run = () => actions.run.mutate(
    { id: automationId, caseId: GUID.test(ref) ? ref : null, caseNumber: ref && !GUID.test(ref) ? ref : null, dryRun },
    { onSuccess: setResult },
  );
  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) { setResult(null); setCaseRef(""); setDryRun(true); } }}>
      <DialogContent size="md">
        <DialogHeader><DialogTitle>{t("admin:automations.run.title")}</DialogTitle><DialogDescription>{t("admin:automations.run.intro")}</DialogDescription></DialogHeader>
        <div className="grid gap-3">
          <Field label={t("admin:automations.run.case")} hint={t("admin:automations.run.caseHint")} error={caseError}>
            <Input value={caseRef} dir="ltr" className="font-mono" placeholder="GRV-2026-000001" onChange={(e) => setCaseRef(e.target.value)} />
          </Field>
          {recent.length > 0 ? (
            <div className="-mt-1 flex flex-wrap items-center gap-1.5 text-xs">
              <span className="text-muted-foreground">{t("admin:automations.run.recent")}</span>
              {recent.map(([id, number]) => (
                <button key={id} type="button" onClick={() => setCaseRef(number)} className={cn("rounded-md border px-1.5 py-0.5 font-mono transition-colors", ref === number ? "border-aegold-400 bg-aegold-50 text-aegold-900" : "border-sand-200 hover:bg-sand-50")}>{number}</button>
              ))}
            </div>
          ) : null}
          <label className="flex items-center justify-between gap-3 rounded-xl border border-sand-200 p-3">
            <span><span className="flex items-center gap-1.5 text-sm font-medium"><FlaskConical className="size-4 text-aegold-700" /> {t("admin:automations.run.dryRun")}</span><span className="block text-xs text-muted-foreground">{t("admin:automations.run.dryRunHint")}</span></span>
            <Switch checked={dryRun} onCheckedChange={setDryRun} />
          </label>
          {result ? (
            <div className="rounded-xl border border-sand-200 bg-sand-50/60 p-3 text-sm">
              <div className="flex items-center gap-2"><Badge variant={RUN_VARIANT[result.status] ?? "default"} size="sm" dot>{t(`admin:automations.runStatus.${result.status}`, { defaultValue: result.status })}</Badge>{result.caseNumber ? <span className="font-mono text-xs">{result.caseNumber}</span> : null}<span className="ms-auto text-xs text-muted-foreground">{result.durationMs} ms</span></div>
              {result.error ? <p className="mt-1.5 text-xs text-danger-700">{result.error}</p> : null}
              {(result.actions ?? []).length ? <ul className="mt-1.5 space-y-0.5 text-xs">{(result.actions ?? []).map((x, i) => <li key={i}><code className="font-mono text-muted-foreground" dir="ltr">{x.type}</code> · {x.detail ?? x.status}</li>)}</ul> : null}
              <Link to="?tab=runs" className="mt-2 inline-block text-xs font-medium text-aegold-800 hover:underline" onClick={() => onOpenChange(false)}>{t("admin:automations.run.openLog")}</Link>
            </div>
          ) : null}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>{t("admin:common.close")}</Button>
          <Button variant={dryRun ? "outline" : "gold"} disabled={!!caseError} loading={actions.run.isPending} onClick={run}>{dryRun ? <FlaskConical /> : <Play />} {dryRun ? t("admin:automations.run.test") : t("admin:automations.run.now")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
